import React, { isValidElement, useMemo, useCallback, useState } from "react";
import { MarketTemplateProps } from "../types";
import { MarketLayout } from "../components/MarketLayout/MarketLayout";
import { Card } from "components/src/components/Card/Card";
import { transformMarketHeader } from "../js/transformMarketHeaders";
import cx from "components/src/utils/cx";

import get from "lodash-es/get";
import isEmpty from "lodash-es/isEmpty";

const _defaultOnSelectionClick = () => {};

const sportsWithDividers = {
  tennis: "vs",
  americanfootball: "at",
  football: "at",
  baseball: "at",
  basketball: "at",
  hockey: "at",
};

function exists(val) {
  return val !== undefined && val !== null;
}

export const SixPack = ({
  id,
  header,
  marketHeader = {},
  showMarketHeader = true,
  markets = [],
  marketFooter,
  footer = {},
  showBorder = false,
  showVersusDivider = false,
  type = "sixpack",
  versusText = "vs",
  groupBy = "group",
  showLimit = "all",
  filters,
  sport,
  league,
  competitionType,
  canShowLess = true,
  oddsFormat = "american",
  initiallyOpen = true,
  eventDetails,
  showMoreWagers = false,
  hasSGP = false,
  isOddsBoost,
  hasFirebets = false,
  hasLivestream = false,
  mode = "light",
  animation = true,
  enableInternalSkeleton,
  mountHiddenMarkets,
  topPadNoHeader = "cui-pt-sm",
  onSelectionClick = _defaultOnSelectionClick,
  onHeaderClick,
  onEventHeaderClick,
  onTemplateClick,
  updateMarket = () => {},
  updateMarkets = () => {},
  onFilterClick,
  isSelectedBuilder,
  priceDisplayFormatter,
  scrollingMarketsInitialAnchor,
  scrollType,
  onShowMoreClick,
  ...rest
}: MarketTemplateProps) => {
  // Strip non-DOM props that leak through ...rest from parent spreads
  const { colSpacing, ...cardRest } = rest as any;
  /**
   * State
   */
  const [templateClicked, setTemplateClicked] = useState(false);
  const [isOpen, setIsOpen] = useState(initiallyOpen);
  const [hasBeenToggled, setHasBeenToggled] = useState(false);

  /**
   * Computed Props for MarketHeader and MarketFooter
   */

  const cardClasses = useMemo(()=>{
    return cx({
        [topPadNoHeader as string]: typeof topPadNoHeader === "string" && (typeof header !== "object" || (!!header && !header?.hasHeader))
      },"cui-cursor-pointer")
  },[topPadNoHeader, header]);

  const hasMultipleCompetitors =
    Array.isArray(markets[0]?.competitors) && markets[0]?.competitors?.length === 2;

  const _marketHeader = useMemo(() => {
    return transformMarketHeader({ markets, marketHeader, sport, league, competitionType, hasMultipleCompetitors });
  }, [marketHeader, sport, league, competitionType, hasMultipleCompetitors]);

  const _footer = useMemo(() => {
    let marketFooterProps = {
      moreWagers: showMoreWagers,
      hasSGP,
      hasFirebets,
      hasLivestream,
      eventDetails,
    };
    if (typeof footer === "object") {
      marketFooterProps = { ...footer, ...marketFooterProps };
    }
    return marketFooterProps;
  }, [footer, eventDetails, hasSGP, showMoreWagers, hasLivestream]);

  /**
   * Event Handlers
   */
  const _onTemplateClick = useCallback(
    (e) => {
      if (
        e.target?.offsetParent?.nodeName !== "BUTTON" &&
        e.target?.nodeName !== "BUTTON"
      ) {
        onTemplateClick && onTemplateClick(e);
      }
    },
    [onTemplateClick]
  );

  const _onToggle = useCallback((payload) => {
    const { isOpen, id } = payload;
    setIsOpen(isOpen);
    if (!hasBeenToggled) setHasBeenToggled(true);
    onHeaderClick && onHeaderClick(payload);
  }, []);

  /** Flags
   *
   */

  const hasHeader = !isEmpty(header);

  const _hasMarketHeader = showMarketHeader && !!marketHeader;

  // Temporarily disabled
  const _showVersusDivider =
    (sport && sport in sportsWithDividers) || showVersusDivider;
  const _verusText =
    _showVersusDivider && sport ? sportsWithDividers[sport] : versusText;

  // const _header = { ...header, initiallyOpen };
  // const _collapsible =
  //   typeof header?.collapsible === "boolean" ? header?.collapsible : true;
  const hasScore = eventDetails?.isLive ||
    (exists(eventDetails?.home?.score) && exists(eventDetails?.away?.score)) ||
    (exists(eventDetails?.competitor1?.score) &&
      exists(eventDetails?.competitor2?.score)) ||
    (exists(get(markets, "[0].competitor.score", null)) &&
      exists(get(markets, "[1].competitor.score", null))) ||
    (exists(get(markets, "[0].competitors[0].score", null)) &&
      exists(get(markets, "[0].competitors[1].score", null)));

  return (
    <Card
      preventHeaderClickPropagation={true}
      {...cardRest}
      id={`${id}`}
      {...header?.computedHeader}
      collapsible={header?.collapsible}
      className={cardClasses}
      showBorder={showBorder}
      initiallyOpen={initiallyOpen}
      onToggle={_onToggle}
      onCardClick={_onTemplateClick}
      animation={animation}
    >
      {header && header?.isReactNode ? (<Card.Header>{header?.component}</Card.Header>) : null}

      {_hasMarketHeader ? (
        <Card.SubHeader>
          <MarketLayout.Header
            {...{..._marketHeader, labels: hasMultipleCompetitors ? [] : _marketHeader?.labels} }
            noBorder
            className="cui-mb-1"
          />
        </Card.SubHeader>
      ) : null}

      <MarketLayout.Markets
        markets={markets}
        marketTemplateType="sixpack"
        marketHeader={_marketHeader}
        showVersusDivider={showVersusDivider}
        sport={sport}
        league={league}
        competitionType={competitionType}
        mode={mode}
        hasScore={hasScore}
        versusText={versusText}
        updateMarket={updateMarket}
        onSelectionClick={onSelectionClick}
        isSelectedBuilder={isSelectedBuilder}
        priceDisplayFormatter={priceDisplayFormatter}
      />
      {isValidElement(footer) ? (
        <MarketLayout.Footer>{footer}</MarketLayout.Footer>
      ) : _footer && Object.keys(_footer).length ? (
        <MarketLayout.Footer {..._footer} className="cui-mt-1" />
      ) : null}
    </Card>
  );
};
